import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Volume2, Menu, Info, Maximize2, Minimize2 } from "lucide-react";
import PhilosophyPage from "./components/PhilosophyPage";
import ModeInfo from "./components/ModeInfo";
import MeditationMode from "./components/MeditationMode";
import HealingMode from "./components/HealingMode";
import AttuneMode from "./components/AttuneMode";
import { audioEngine } from "./lib/audioEngine";
import StarInfo from "./components/StarInfo";
import { useSavedState } from "./lib/savedState";
import { ScrollHint } from "./components/ScrollHint";

const MODES = [
  { id: "meditation", label: "Meditation" },
  { id: "healing", label: "Healing" },
  { id: "attune", label: "Attune" },
];

function Starfield() {
  const stars = useMemo(
    () =>
      Array.from({ length: 120 }).map((_, i) => ({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * 4,
        dur: Math.random() * 3 + 2,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {stars.map((s) => (
        <span
          key={s.id}
          className="absolute rounded-full bg-white"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// Rotating night-sky layer: an oversized square (always covers the viewport) that
// holds the twinkling stars + winter constellations and wheels very slowly around
// the central figure, like the real sky turning about the celestial pole.
function SkyLayer({ onSelect }) {
  const [hovered, setHovered] = useState(null);
  // One shared rotation drives the sky; labels use the exact negative so they are
  // ALWAYS upright (no two-animation drift), while still orbiting with their stars.
  const rot = useMotionValue(0);
  const counterRot = useTransform(rot, (v) => -v);
  useEffect(() => {
    const controls = animate(rot, 360, { duration: 1600, ease: "linear", repeat: Infinity });
    return () => controls.stop();
  }, [rot]);
  const layerStyle = { width: "185vmax", height: "185vmax", x: "-50%", y: "-50%", rotate: rot };
  return (
    <>
      <motion.div className="pointer-events-none fixed left-1/2 top-1/2" style={layerStyle} data-testid="sky-layer">
        <Starfield />
        <WinterSky counterRot={counterRot} hovered={hovered} />
      </motion.div>
      {/* invisible tap targets riding the same rotation, above the stage but below the header */}
      <motion.div data-sky-hit className="pointer-events-none fixed left-1/2 top-1/2" style={{ ...layerStyle, zIndex: 15 }}>
        <div className="pointer-events-none absolute" style={SKY_BOX_STYLE}>
          <svg viewBox="0 0 100 100" className="h-full w-full" style={{ overflow: "visible" }}>
            {SKY_LABELS.map((l) => (
              <SkyLabel key={l.id} label={l} counterRot={counterRot} onSelect={onSelect} onHover={setHovered} />
            ))}
          </svg>
        </div>
      </motion.div>
    </>
  );
}

const SKY_BOX_STYLE = {
  left: "50%",
  top: "50%",
  width: "min(38vw, 440px)",
  aspectRatio: "1 / 1",
  transform: "translate(calc(-50% + 27vw), calc(-50% - 15vh))",
  zIndex: 1,
};

// Winter-sky trio: Orion, Sirius and the Pleiades in correct relative orientation.
// anchor (ax, ay) = star position; (dx, dy) = screen-space offset of the label from it.
// Orion is anchored at its centre and its label rides just above whichever star is topmost on screen.
const ORION_PTS = [[30.6, 39.1], [45.5, 41.3], [40.6, 34.1], [37.8, 57.6], [40.2, 56.2], [42.3, 54.4], [34.2, 73], [50.9, 70]];
const SKY_LABELS = [
  { id: "orion", ax: 40.6, ay: 53.5, dx: 0, dy: -5.1, t: "ORION", pts: ORION_PTS, hitR: 22 },
  { id: "sirius", ax: 6, ay: 86.9, dx: 6, dy: 4.1, t: "SIRIUS", hitR: 6 },
  { id: "pleiades", ax: 91.7, ay: 6.8, dx: 0, dy: 8.2, t: "PLEIADES", hitR: 7 },
];

function SkyLabel({ label, counterRot, onSelect, onHover, hovered }) {
  const ref = useRef(null);
  const textRef = useRef(null);
  useEffect(() => {
    const set = (r) => {
      ref.current?.setAttribute("transform", `translate(${label.ax} ${label.ay}) rotate(${r})`);
      if (label.pts && textRef.current) {
        const a = (-r * Math.PI) / 180;
        const top = Math.min(...label.pts.map(([x, y]) => (x - label.ax) * Math.sin(a) + (y - label.ay) * Math.cos(a)));
        textRef.current.setAttribute("y", top + label.dy);
      }
    };
    set(counterRot.get());
    return counterRot.on("change", set);
  }, [counterRot, label]);
  if (onSelect)
    return (
      <g
        ref={ref}
        data-testid={`sky-tap-${label.id}`}
        onClick={(e) => {
          // let taps reach any button underneath (chakra orbs, controls) before opening the star card
          const under = document
            .elementsFromPoint(e.clientX, e.clientY)
            .find((el) => !el.closest("[data-sky-hit]") && el.closest("button, a, input, [role=button]"));
          if (under) return under.closest("button, a, input, [role=button]").click();
          onSelect(label.id);
        }}
        onMouseEnter={() => onHover(label.id)}
        onMouseLeave={() => onHover(null)}
        style={{ pointerEvents: "all", cursor: "pointer" }}
      >
        <circle r={label.hitR} fill="transparent" />
        <text ref={textRef} x={label.dx} y={label.dy} fontSize="3" textAnchor="middle" fill="transparent" stroke="transparent" strokeWidth="4">
          {label.t}
        </text>
      </g>
    );
  return (
    <g ref={ref}>
      <text
        ref={textRef}
        data-testid={`label-${label.id}`}
        data-hovered={hovered ? "true" : "false"}
        x={label.dx}
        y={label.dy}
        fontSize="3"
        style={{
          fill: hovered ? "rgba(235,242,255,0.98)" : "rgba(175,195,255,0.55)",
          filter: hovered ? "drop-shadow(0 0 1.2px rgba(170,200,255,0.95))" : "none",
          transition: "fill 0.4s ease, filter 0.4s ease",
        }}
      >
        {label.t}
      </text>
    </g>
  );
}
// Coordinates derived from real RA/Dec (higher RA = left, higher Dec = up), projected
// into a square SVG so the shape stays true on any screen aspect.
function WinterSky({ counterRot, hovered }) {
  const STAR_COLOR = "#e3ecff"; // uniform star colour everywhere
  const named = [
    { id: "betelgeuse", x: 30.6, y: 39.1, r: 1.5, c: STAR_COLOR },
    { id: "bellatrix", x: 45.5, y: 41.3, r: 1.1, c: STAR_COLOR },
    { id: "meissa", x: 40.6, y: 34.1, r: 0.85, c: STAR_COLOR }, // the head
    { id: "alnitak", x: 37.8, y: 57.6, r: 1.0, c: STAR_COLOR },
    { id: "alnilam", x: 40.2, y: 56.2, r: 1.15, c: STAR_COLOR },
    { id: "mintaka", x: 42.3, y: 54.4, r: 1.0, c: STAR_COLOR },
    { id: "saiph", x: 34.2, y: 73.0, r: 1.1, c: STAR_COLOR },
    { id: "rigel", x: 50.9, y: 70.0, r: 1.5, c: STAR_COLOR }, // blue-white foot
    { id: "sirius", x: 6, y: 86.9, r: 2.0, c: STAR_COLOR }, // brightest star in the sky
  ];
  const pleiades = [
    [92, 7], [90.4, 6], [93.2, 5.4], [91, 8.6], [93.6, 8.1], [89.7, 7.9], [91.9, 4.9],
  ];
  return (
    <div
      className="pointer-events-none absolute"
      style={SKY_BOX_STYLE}
      data-testid="winter-sky"
    >
      <svg viewBox="0 0 100 100" className="h-full w-full" style={{ overflow: "visible" }}>
        <defs>
          <filter id="starGlow" x="-300%" y="-300%" width="700%" height="700%">
            <feGaussianBlur stdDeviation="1.1" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* orientation pointers: Orion's belt down-left to Sirius, up-right to the Pleiades */}
        <g stroke="rgba(150,180,255,0.16)" strokeWidth="0.3" strokeDasharray="1.2 1.6" fill="none">
          <line x1="37.8" y1="57.6" x2="6" y2="86.9" />
          <line x1="42.3" y1="54.4" x2="92" y2="7" />
        </g>

        {/* Orion — true asterism: head, shoulders, body, belt and legs */}
        <g stroke="rgba(200,215,255,0.42)" strokeWidth="0.45" fill="none" strokeLinejoin="round" strokeLinecap="round">
          <polyline points="30.6,39.1 40.6,34.1 45.5,41.3" /> {/* shoulders via head */}
          <line x1="30.6" y1="39.1" x2="37.8" y2="57.6" /> {/* left body */}
          <line x1="45.5" y1="41.3" x2="42.3" y2="54.4" /> {/* right body */}
          <line x1="37.8" y1="57.6" x2="34.2" y2="73" /> {/* left leg */}
          <line x1="42.3" y1="54.4" x2="50.9" y2="70" /> {/* right leg */}
          <polyline points="37.8,57.6 40.2,56.2 42.3,54.4" stroke="rgba(224,233,255,0.75)" strokeWidth="0.5" /> {/* belt */}
        </g>

        {/* named bright stars — twinkle in brightness */}
        {named.map((s, i) => (
          <motion.circle
            key={s.id}
            data-testid={`star-${s.id}`}
            cx={s.x}
            cy={s.y}
            r={s.r}
            fill={s.c}
            filter="url(#starGlow)"
            initial={{ opacity: 0.8 }}
            animate={{ opacity: [0.8, 1, 0.82] }}
            transition={{ duration: 3 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
          />
        ))}

        {/* Pleiades — tight cluster wrapped in a soft blue nebulosity */}
        <circle cx="91.7" cy="6.8" r="6" fill="rgba(120,160,255,0.1)" filter="url(#starGlow)" />
        {pleiades.map(([x, y], i) => (
          <motion.circle
            key={`pleiad-${x}-${y}`}
            cx={x}
            cy={y}
            r={0.55}
            fill={STAR_COLOR}
            filter="url(#starGlow)"
            animate={{ opacity: [0.6, 1, 0.7] }}
            transition={{ duration: 2.6 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          />
        ))}

        {/* labels — anchored to their star, offset in screen space so they sit identically at any rotation */}
        <g fill="rgba(175,195,255,0.55)" textAnchor="middle" style={{ fontFamily: "ui-monospace, monospace", letterSpacing: "0.06em" }}>
          {SKY_LABELS.map((l) => (
            <SkyLabel key={l.id} label={l} counterRot={counterRot} hovered={hovered === l.id} />
          ))}
        </g>
      </svg>
    </div>
  );
}

function App() {
  const [mode, setMode] = useState("meditation");
  const [star, setStar] = useState(null);
  const [showPhilosophy, setShowPhilosophy] = useState(false);
  const [guideSection, setGuideSection] = useState(null);
  const [modeInfo, setModeInfo] = useState(null);
  const openGuide = (id) => {
    setGuideSection(id);
    setShowPhilosophy(true);
  };
  const [volume, setVolume] = useSavedState("volume", 0.55);

  useEffect(() => {
    audioEngine.setVolume(volume);
  }, [volume]);

  const switchMode = (m) => {
    audioEngine.stop(0.4);
    setMode(m);
  };

  const [focus, setFocus] = useState(false);
  const enterFocus = () => {
    setFocus(true);
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };
  const exitFocus = () => {
    setFocus(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };
  useEffect(() => {
    if (!focus) return;
    const onKey = (e) => e.key === "Escape" && exitFocus();
    const onFs = () => !document.fullscreenElement && setFocus(false);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, [focus]);

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#030308] text-slate-100">
      {/* cosmic backdrop */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(40,30,80,0.55), rgba(3,3,8,0) 60%), radial-gradient(ellipse at 80% 80%, rgba(20,50,90,0.35), rgba(3,3,8,0) 55%)",
        }}
      />
      <SkyLayer onSelect={setStar} />
      <StarInfo starId={star} onClose={() => setStar(null)} />
      <PhilosophyPage open={showPhilosophy} section={guideSection} onClose={() => setShowPhilosophy(false)} />
      <ModeInfo modeId={modeInfo} onClose={() => setModeInfo(null)} />

      {/* header */}
      <header className={`relative z-20 flex flex-col items-center gap-3 px-5 pb-4 pt-[max(1rem,env(safe-area-inset-top))] transition-opacity duration-500 sm:px-8 lg:block ${focus ? "pointer-events-none opacity-0" : ""}`}>
        <button
          data-testid="philosophy-menu-button"
          onClick={() => openGuide(null)}
          aria-label="Open philosophy"
          className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300 transition-colors hover:bg-white/15 hover:text-white sm:left-8 lg:top-1/2 lg:-translate-y-1/2"
        >
          <Menu size={16} />
        </button>
        <div data-testid="app-title" className="flex items-center justify-center gap-1.5 text-center sm:gap-2">
          <img src="/breathtones-icon.png" alt="BreathTones" className="h-11 w-11 shrink-0 sm:h-12 sm:w-12" data-testid="app-icon" />
          <h1 className="flex flex-col items-start font-serif tracking-wide text-white sm:block sm:text-2xl">
            <span className="text-2xl leading-tight sm:text-2xl">BreathTones</span>
            <span className="hidden sm:inline">: </span>
            <span className="text-sm leading-tight text-slate-300 sm:text-2xl sm:text-white">Tuning Body: Mind : Spirit</span>
          </h1>
        </div>

        <div className="flex items-center gap-3 lg:absolute lg:right-8 lg:top-1/2 lg:-translate-y-1/2">
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2">
            <Volume2 size={16} className="text-slate-300" />
            <input
              data-testid="volume-slider"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="h-1 w-20 cursor-pointer accent-fuchsia-400"
            />
          </div>
          <button
            data-testid="focus-enter"
            onClick={enterFocus}
            aria-label="Full screen"
            className="flex items-center rounded-full border border-white/10 bg-white/5 p-2.5 text-slate-300 transition-colors hover:bg-white/15 hover:text-white"
          >
            <Maximize2 size={15} />
          </button>
        </div>
      </header>

      {/* mode switcher */}
      <div className={`relative z-20 flex justify-center px-4 transition-opacity duration-500 ${focus ? "pointer-events-none opacity-0" : ""}`}>
        <div className="flex gap-1 rounded-full border border-white/10 bg-slate-950/60 p-1 backdrop-blur-xl">
          {MODES.map((m) => (
            <div key={m.id} className="relative">
              <button
                data-testid={`mode-${m.id}`}
                onClick={() => switchMode(m.id)}
                className="relative rounded-full py-2 pl-4 pr-8 text-sm font-medium transition-colors sm:pl-7 sm:pr-10"
              >
                {mode === m.id && (
                  <motion.span
                    layoutId="mode-pill"
                    className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500/80 to-fuchsia-500/80"
                    transition={{ type: "spring", damping: 26, stiffness: 300 }}
                  />
                )}
                <span className={`relative ${mode === m.id ? "text-white" : "text-slate-400"}`}>{m.label}</span>
              </button>
              <button
                data-testid={`mode-info-${m.id}`}
                onClick={() => setModeInfo(m.id)}
                aria-label={`About ${m.label}`}
                className={`info-hit absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full transition-colors sm:right-3 ${mode === m.id ? "text-white/80 hover:bg-white/20 hover:text-white" : "text-slate-500 hover:bg-white/10 hover:text-white"}`}
              >
                <Info size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* stage */}
      <main className={focus ? "fixed inset-0 z-30 flex flex-col" : "relative z-10 flex flex-1 flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-0 sm:px-8 sm:pb-8 sm:pt-6"}>
        {mode === "meditation" && <MeditationMode volume={volume} focus={focus} onEngage={enterFocus} />}
        {mode === "healing" && <HealingMode volume={volume} focus={focus} onEngage={enterFocus} />}
        {mode === "attune" && <AttuneMode volume={volume} focus={focus} onEngage={enterFocus} />}
      </main>
      <ScrollHint hidden={focus} mode={mode} />
      {focus && (
        <button
          data-testid="focus-exit"
          onClick={exitFocus}
          aria-label="Exit full screen"
          className="fixed right-[max(1rem,env(safe-area-inset-right))] top-[max(1rem,env(safe-area-inset-top))] z-50 rounded-full border border-white/10 bg-white/5 p-2.5 text-slate-400 opacity-60 transition-opacity hover:bg-white/15 hover:text-white hover:opacity-100"
        >
          <Minimize2 size={16} />
        </button>
      )}
    </div>
  );
}

export default App;
